import type { BoardDto, StudentDto } from '@mm/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tldraw, type Editor, type TLAssetStore } from 'tldraw';
import 'tldraw/tldraw.css';
import { getBoard, renameBoard, uploadBoardAsset } from '../api/boards';
import { getStudent } from '../api/students';
import { useAuth } from '../auth/AuthContext';
import { homeForRole } from '../auth/RequireRole';
import { useActiveTime } from '../hooks/useActiveTime';
import { useBoardSync } from '../hooks/useBoardSync';
import { exportBoard, type ExportKind, type ExportScope } from '../lib/export-pdf';
import { useTheme } from '../theme/ThemeContext';
import { IconArrowLeft, IconChevronDown, IconDownload, IconPencil } from '../ui/icons';
import { ThemeToggle } from '../ui/ThemeToggle';
import { StudentAssignmentPanel } from './board/StudentAssignmentPanel';
import { TutorAssignmentPanel } from './board/TutorAssignmentPanel';

export function BoardPage() {
  const { id } = useParams();
  const { scheme } = useTheme();
  const { user } = useAuth();
  const role = user?.role;
  const isTutor = role === 'TUTOR' || role === 'ADMIN';
  const isStudent = role === 'STUDENT';

  const [board, setBoard] = useState<BoardDto | null>(null);
  const [student, setStudent] = useState<StudentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  const exportMenuRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getBoard(id)
      .then((b) => {
        if (!cancelled) {
          setBoard(b);
          setLoading(false);
          // Карточку ученика грузим только у репетитора — у STUDENT нет прав
          // на /api/students, и запрос бы вернул 403.
          if (isTutor) {
            getStudent(b.studentId)
              .then((s) => {
                if (!cancelled) setStudent(s);
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, isTutor]);

  const assetStore: TLAssetStore = useMemo(
    () => ({
      async upload(_a, file) {
        const { url } = await uploadBoardAsset(id!, file);
        return { src: url };
      },
      resolve(a) {
        return a.props.src ?? null;
      },
    }),
    [id],
  );

  const sync = useBoardSync({ boardId: id ?? '', assets: assetStore });

  // Хоткеи: Ctrl/Cmd+E для PDF
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const inField = !!target?.closest('input, textarea, [contenteditable="true"]');
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'e') {
        const ed = editorRef.current;
        if (!ed || inField) return;
        e.preventDefault();
        const scope: ExportScope = ed.getSelectedShapeIds().length > 0 ? 'selection' : 'page';
        void exportBoard(ed, 'pdf', scope, board?.title ?? 'board');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [board?.title]);

  // Синхронизация темы с tldraw editor
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.user.updateUserPreferences({ colorScheme: scheme });
  }, [scheme]);

  // Авто-статусы + тайм-трекер — только для STUDENT на assignment-доске.
  useActiveTime({
    boardId: board?.id ?? null,
    enabled: isStudent && board?.mode === 'ASSIGNMENT',
    store: sync.store ?? null,
    status: board?.status ?? null,
    onMarked: () => {
      if (!id) return;
      getBoard(id)
        .then(setBoard)
        .catch(() => {});
    },
  });

  async function onExport(kind: ExportKind, scope: ExportScope) {
    const ed = editorRef.current;
    if (!ed) return;
    exportMenuRef.current?.removeAttribute('open');
    try {
      await exportBoard(ed, kind, scope, board?.title ?? 'board');
    } catch (err) {
      console.error('export failed', err);
      alert('Не удалось сохранить файл.');
    }
  }

  async function onRename() {
    if (!board) return;
    const next = prompt('Новое название', board.title);
    if (next == null || next.trim() === '' || next === board.title) return;
    const updated = await renameBoard(board.id, next.trim());
    setBoard(updated);
  }

  if (loading)
    return (
      <div className="fixed inset-0 flex items-center justify-center text-muted text-[13px]">
        Загрузка…
      </div>
    );

  if (notFound || !board) {
    const backTo = isStudent ? '/app/my' : '/app/students';
    const backLabel = isStudent ? 'к заданиям' : 'к ученикам';
    return (
      <main className="max-w-xl mx-auto p-8 space-y-2">
        <p className="text-fg">Доска не найдена.</p>
        <Link to={backTo} className="text-muted hover:text-fg text-[12px]">
          ← {backLabel}
        </Link>
      </main>
    );
  }

  const showSidePanel = isTutor || (isStudent && board.mode === 'ASSIGNMENT');

  return (
    <div className="fixed inset-0 flex flex-col bg-bg">
      <header className="flex items-center justify-between gap-3 px-4 h-[42px] border-b border-border bg-bg shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {isTutor ? (
            <Link
              to={`/app/students/${board.studentId}`}
              className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-fg whitespace-nowrap max-w-[200px] truncate"
              title={student?.fullName ?? 'К ученику'}
            >
              <IconArrowLeft size={12} className="shrink-0" />
              <span className="truncate">{student?.fullName ?? 'к ученику'}</span>
            </Link>
          ) : (
            <Link
              to={homeForRole(role ?? 'STUDENT')}
              className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-fg whitespace-nowrap"
            >
              <IconArrowLeft size={12} className="shrink-0" />
              <span>к заданиям</span>
            </Link>
          )}
          <span className="text-border">·</span>
          <h1
            className="font-display text-[17px] leading-none font-medium truncate"
            title={board.title}
            style={{ fontVariationSettings: '"opsz" 36, "SOFT" 10' }}
          >
            {board.title}
          </h1>
          {isTutor ? (
            <button
              onClick={onRename}
              aria-label="Переименовать"
              title="Переименовать"
              className="text-muted hover:text-accent p-1 rounded"
            >
              <IconPencil size={13} />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-1">
          <ThemeToggle />
          <details ref={exportMenuRef} className="relative">
            <summary
              className="list-none cursor-pointer inline-flex items-center justify-center gap-1.5 rounded text-[13px] font-medium border border-border bg-bg text-fg h-8 px-3 hover:bg-row-hover"
              style={{ listStyle: 'none' }}
            >
              <IconDownload size={13} />
              Скачать
              <IconChevronDown size={12} />
            </summary>
            <div className="absolute right-0 mt-1 w-[220px] rounded border border-border bg-bg shadow-lg py-1 text-[13px] z-[1000]">
              <button
                className="block w-full text-left px-3 py-2 hover:bg-row-hover"
                onClick={() => onExport('pdf', 'page')}
              >
                PDF — вся доска
              </button>
              <button
                className="block w-full text-left px-3 py-2 hover:bg-row-hover"
                onClick={() => onExport('pdf', 'selection')}
              >
                PDF — выделенное
              </button>
              <div className="my-1 border-t border-border-soft" />
              <button
                className="block w-full text-left px-3 py-2 hover:bg-row-hover"
                onClick={() => onExport('png', 'page')}
              >
                PNG
              </button>
              <button
                className="block w-full text-left px-3 py-2 hover:bg-row-hover"
                onClick={() => onExport('svg', 'page')}
              >
                SVG
              </button>
              <div className="my-1 border-t border-border-soft" />
              <p className="px-3 py-1 text-[11px] text-muted font-caps italic">
                Ctrl/⌘+E · быстрый PDF
              </p>
            </div>
          </details>
        </div>
      </header>
      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative">
          {sync.status === 'loading' ? (
            <div className="fixed inset-0 flex items-center justify-center text-muted text-[13px]">
              Подключаемся…
            </div>
          ) : sync.status === 'error' ? (
            <div className="fixed inset-0 flex items-center justify-center flex-col gap-2">
              <p className="text-fg">Соединение потеряно.</p>
              <button
                onClick={() => window.location.reload()}
                className="text-accent underline"
              >
                Перезагрузить
              </button>
            </div>
          ) : (
            <Tldraw
              store={sync.store}
              onMount={(ed) => {
                editorRef.current = ed;
                ed.user.updateUserPreferences({ locale: 'ru', colorScheme: scheme });
              }}
            />
          )}
        </div>
        {showSidePanel ? (
          isTutor ? (
            <TutorAssignmentPanel board={board} onChange={setBoard} />
          ) : (
            <StudentAssignmentPanel board={board} onChange={setBoard} />
          )
        ) : null}
      </div>
    </div>
  );
}
